import { FC, useEffect, useState } from 'react';
import { Button, Modal, Form, Spin, message, Divider, Tooltip } from 'antd';
import '../../../../components/Modals/Modals.scss';
import close from '../../../../assets/RoundedClose.svg';
import { PlusCircleOutlined } from '@ant-design/icons';
import * as Yup from 'yup';
import { FloatInput } from '../../../../components/Fields/FloatInput';
import { yupSync } from '../../../../utils';
import Delete from '../../../../components/Icons/Delete';
import { FloatTextArea } from '../../../../components/Fields/FloatTextArea';
import {
    useCreateExpenseAreaMutation,
    useLazyGetExpenseAreaQuery,
    useUpdateExpenseAreaMutation
} from '../../../../api/expenseAreas';
import Edit from '../../../../components/Icons/Edit';
import { useSearchParams } from 'react-router-dom';
import { useAppSelector } from '../../../../hooks';
import clocoIcon from '../../../../assets/GradeIcon.svg';
import { PeopleIcon } from '../../../../components/Icons/PeopleIcon';

interface ModalProps {
    openModal: boolean;
    setOpenModal: React.Dispatch<React.SetStateAction<{ type: string; open: boolean; id: number | null }>>;
    type: string;
    id: number | null;
    refetch: () => void;
    dataEA?: any;
}

const validationSchema = Yup.object().shape({
    name: Yup.string().required(' This field is required')
});

interface ComponentI {
    name: string;
    description: string;
    localId: string;
}

export const AddExpenseAreaModal: FC<ModalProps> = ({ openModal, setOpenModal, type, id, refetch, dataEA }) => {
    const [createExpenseArea] = useCreateExpenseAreaMutation();
    const [updateExpenseArea] = useUpdateExpenseAreaMutation();
    const [getExpenseArea, { data, isLoading }] = useLazyGetExpenseAreaQuery();
    const [changed, setChanged] = useState(false);
    const [components, setComponents] = useState<ComponentI[] | []>([]);
    const [errors, setErrors] = useState<number[]>([]);
    const [form] = Form.useForm();
    const [isConfirmModal, setIsConfirmModal] = useState(false);
    const [indexComponentToDelete, setIndexComponentToDelete] = useState<null | number>(null);
    const [_searchParams, setSearchParams] = useSearchParams();
    const { role } = useAppSelector((state) => state.profileReducer);

    useEffect(() => {
        if (type === 'edit' && id) {
            getExpenseArea(id).then((res: any) => {
                form.setFieldsValue({
                    name: res?.data?.data?.name
                });
                setComponents(res?.data?.data?.components);
            });
        }
    }, [type, id]);

    const forms =
        type === 'edit'
            ? (data?.data?.forms as Record<string, any> | undefined)
            : undefined;

    const renderDocumentFormMeta = (formPart: Record<string, any> | undefined) => {
        if (!formPart) return null;
        if (formPart.status === 'draft') {
            return (
                <div className="EA__add-info">
                    <div className="EA__status">Draft</div>
                </div>
            );
        }
        return (
            <div className="EA__add-info">
                {!!formPart.updated_at && (
                    <Tooltip title={`Last update on ${formPart.updated_at}`}>
                        <div className="EA__time">
                            <img src={clocoIcon} alt="" /> {formPart.updated_at}
                        </div>
                    </Tooltip>
                )}
                {!!formPart.number_of_suppliers && (
                    <div className="EA__people">
                        <PeopleIcon fill="#4D5E93" /> {formPart.number_of_suppliers}
                    </div>
                )}
            </div>
        );
    };

    const handleFormSubmit = async () => {
        let isValid = true;
        const errorIndices: number[] = [];

        components?.forEach((component: ComponentI, index: number) => {
            if (!component.name) {
                errorIndices.push(index);
                isValid = false;
            }
        });
        setErrors(errorIndices);

        form.validateFields()
            .then((values: any) => {
                if (!isValid) return;

                const cleanedComponents = components.map(({ localId, ...rest }) => rest);

                const baseData = {
                    //@ts-ignore
                    ...(type === 'edit' && { id: data?.data?.id }),
                    data: {
                        name: values?.name,
                        components: cleanedComponents
                    }
                };

                if (type === 'create') {
                    createExpenseArea(baseData)
                        ?.unwrap()
                        ?.then((res: any) => {
                            message.success(res?.message);
                            setOpenModal({ open: false, type: '', id: null });
                            setSearchParams('');
                            refetch();
                        })
                        .catch((error) => {
                            message?.error(error?.data?.message);
                        });
                }
                if (type === 'edit') {
                    updateExpenseArea(baseData)
                        ?.unwrap()
                        ?.then((res: any) => {
                            message.success(res?.message);
                            setOpenModal({ open: false, type: '', id: null });
                            setSearchParams('');
                            refetch();
                        })
                        .catch((error) => {
                            message?.error(error?.data?.message);
                        });
                }
            })
            .catch((err) => {
                console.log('err', err);
            });
    };

    const deleteComponent = (index: number) => {
        setChanged(true);
        const array = [...components];
        array.splice(index, 1);
        setComponents(array);
        setIndexComponentToDelete(null);
    };

    const updateComponentField = (
        index: number,
        key: keyof Pick<ComponentI, 'name' | 'description'>,
        value: string
    ) => {
        setChanged(true);
        setComponents((prev) => prev.map((component, i) => (i === index ? { ...component, [key]: value } : component)));
        setErrors((prev) => prev.filter((i) => i !== index));
    };

    return (
        <>
            <Modal
                destroyOnClose
                className={`modal-area`}
                centered
                open={openModal}
                onCancel={() => {
                    if (changed) {
                        setIsConfirmModal(true);
                    } else {
                        setOpenModal({ open: false, type: '', id: null });
                        setSearchParams('');
                    }
                }}
                closeIcon={<img src={close} />}
                width={type === 'view' ? 600 : 800}
                footer={
                    type === 'view' ? (
                        <></>
                    ) : (
                        <div style={{ display: 'flex', justifyItems: 'space-between', gap: '16px', width: '100%' }}>
                            <Button
                                block
                                key="back"
                                onClick={() => {
                                    if (changed) {
                                        setIsConfirmModal(true);
                                    } else {
                                        setOpenModal({ open: false, type: '', id: null });
                                        setSearchParams('');
                                    }
                                }}
                                className="cancel"
                            >
                                Cancel
                            </Button>
                            <Button form="schoolForm" htmlType="submit" type="primary" block onClick={handleFormSubmit}>
                                {type === 'create' ? 'Create' : type === 'edit' ? 'Save Changes' : ''}
                            </Button>
                        </div>
                    )
                }
            >
                {' '}
                {type === 'view' ? (
                    <Spin spinning={isLoading} size="large">
                        <div className="modal">
                            <div className="modal__header addsuppliermodal">
                                <div className="modal__title">Expense Area Overview</div>
                            </div>
                            <div className="modal__main main" style={{ padding: '40px 40px 30px 60px' }}>
                                <div className="main__EA-name">
                                    {dataEA?.name}{' '}
                                    {role === 'super_admin' && (
                                        <div
                                            className="main__EA-name-edit"
                                            onClick={() => {
                                                window.open(`/admin-panel/expense_areas?id=${id}&type=edit`, '_blank');
                                            }}
                                        >
                                            <Edit fill="#808CB2" />
                                        </div>
                                    )}
                                </div>
                                <div className="main__components">
                                    {dataEA?.components?.map((elem: ComponentI, index: number) => {
                                        return (
                                            <div className="EA__component without-icon" key={index}>
                                                {elem?.name}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </Spin>
                ) : (
                    <Spin spinning={isLoading} size="large">
                        <div className="modal">
                            <div className="modal__header addsuppliermodal">
                                <div className="modal__title">
                                    {type === 'create'
                                        ? 'Add Expense Area'
                                        : type === 'edit'
                                        ? 'Edit Expense Area'
                                        : ''}
                                </div>
                            </div>
                            <div
                                className="modal__main main"
                                style={{ padding: '15px 56px 43px 56px', minHeight: '500px' }}
                            >
                                <div className="main__row">
                                    <div className="main__expenseAreaBlock expenseAreaBlock first">
                                        <Form form={form}>
                                            <Form.Item
                                                className="input-wrapper"
                                                name="name"
                                                rules={yupSync('name', validationSchema, true)}
                                            >
                                                <FloatInput
                                                    floatLabel="Expense Area Name"
                                                    className="input"
                                                    required
                                                    maxLength={60}
                                                    onInput={() => setChanged(true)}
                                                />
                                            </Form.Item>
                                        </Form>
                                        <Divider />
                                        <Button
                                            className="modal__duration-item slot"
                                            onClick={() =>
                                                setComponents((prev) => [
                                                    ...prev,
                                                    {
                                                        name: '',
                                                        description: '',
                                                        localId: `${Date.now()}-${Math.random()}`
                                                    }
                                                ])
                                            }
                                            disabled={false}
                                            icon={<PlusCircleOutlined />}
                                        >
                                            Add Component
                                        </Button>
                                        <div className="modal-area__components">
                                            {components?.map((elem, index) => {
                                                return (
                                                    <>
                                                        <div className={`slot`}>
                                                            <div className="slot__title">
                                                                Component {index + 1}
                                                                <span onClick={() => setIndexComponentToDelete(index)}>
                                                                    <Delete
                                                                        fill="#E80A0F"
                                                                        style={{ cursor: 'pointer' }}
                                                                    />
                                                                </span>
                                                            </div>
                                                            <div className="slot__form">
                                                                <Form.Item className="input-wrapper">
                                                                    <FloatInput
                                                                        floatLabel="Component name"
                                                                        className="input"
                                                                        required
                                                                        maxLength={60}
                                                                        value={elem.name}
                                                                        onChange={(e) =>
                                                                            updateComponentField(
                                                                                index,
                                                                                'name',
                                                                                e.target.value
                                                                            )
                                                                        }
                                                                        hasError={() => errors?.includes(index)}
                                                                        onInput={() => setChanged(true)}
                                                                    />
                                                                </Form.Item>
                                                                {errors.includes(index) && (
                                                                    <div
                                                                        className="error-text"
                                                                        style={{ margin: '-15px 0 10px 0' }}
                                                                    >
                                                                        This field is required{' '}
                                                                    </div>
                                                                )}
                                                                <Form.Item className="input-wrapper">
                                                                    <FloatTextArea
                                                                        floatLabel="Description"
                                                                        className="input"
                                                                        maxLength={512}
                                                                        value={elem.description}
                                                                        onChange={(e: any) =>
                                                                            updateComponentField(
                                                                                index,
                                                                                'description',
                                                                                e.target.value
                                                                            )
                                                                        }
                                                                        hasError={() => {}}
                                                                        onInput={() => setChanged(true)}
                                                                    />
                                                                </Form.Item>
                                                            </div>
                                                        </div>
                                                    </>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    <div className="main__expenseAreaBlock expenseAreaBlock second">
                                        <div className="expenseAreaBlock__documents-block">
                                            <div className="expenseAreaBlock__title">Documents</div>
                                            <div className="expenseAreaBlock__documents">
                                                <div className="expenseAreaBlock__document">
                                                    <div className="EA">
                                                        <div className="EA__wrapper">
                                                            <div className="EA__title">
                                                                {forms?.['supplier-profile-questionnaire']?.name ??
                                                                    'Supplier Profile Questionnaire'}{' '}
                                                                <PlusCircleOutlined
                                                                    style={{ color: '#808CB2' }}
                                                                    onClick={(e) => {
                                                                        window.open(
                                                                            `/supplier-profile-questionnaire-builder/${id}`,
                                                                            '_blank'
                                                                        );
                                                                    }}
                                                                />
                                                            </div>
                                                            {renderDocumentFormMeta(
                                                                forms?.['supplier-profile-questionnaire']
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="expenseAreaBlock__document">
                                                    <div className="EA">
                                                        <div className="EA__wrapper">
                                                            <div className="EA__title">
                                                                {forms?.['kick-off-questionnaire']?.name ??
                                                                    'Kick-Off Questionnaire'}{' '}
                                                                <PlusCircleOutlined
                                                                    style={{ color: '#808CB2' }}
                                                                    onClick={(e) => {
                                                                        window.open(
                                                                            `/kick-off-questionnaire-builder/${id}`,
                                                                            '_blank'
                                                                        );
                                                                    }}
                                                                />
                                                            </div>
                                                            {renderDocumentFormMeta(forms?.['kick-off-questionnaire'])}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="expenseAreaBlock__document">
                                                    <div className="EA">
                                                        <div className="EA__wrapper">
                                                            <div className="EA__title">
                                                                {forms?.['score-card']?.name ?? 'Score Card'}{' '}
                                                                <PlusCircleOutlined style={{ color: '#808CB2' }} />
                                                            </div>
                                                            {renderDocumentFormMeta(forms?.['score-card'])}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Spin>
                )}
            </Modal>
            <Modal
                className={`confirmModal`}
                centered
                open={isConfirmModal}
                closeIcon={<div style={{ display: 'none', cursor: 'context-menu' }}></div>}
                width={600}
                footer={
                    <div style={{ display: 'flex', justifyItems: 'space-between', gap: '16px', width: '100%' }}>
                        <Button key="back" onClick={() => setIsConfirmModal(false)} block className="cancel">
                            Cancel
                        </Button>
                        <button
                            className="quit"
                            onClick={() => {
                                setOpenModal({ open: false, type: '', id: null });
                                setIsConfirmModal(false);
                                setSearchParams('');
                            }}
                        >
                            Quit
                        </button>
                    </div>
                }
            >
                <div className="modal-confirm">Your data won’t be saved! Are you sure you want to quit?</div>
            </Modal>
            <Modal
                className={`confirmModal`}
                centered
                open={indexComponentToDelete === 0 || !!indexComponentToDelete}
                closeIcon={<div style={{ display: 'none', cursor: 'context-menu' }}></div>}
                width={600}
                footer={
                    <div style={{ display: 'flex', justifyItems: 'space-between', gap: '16px', width: '100%' }}>
                        <Button key="back" onClick={() => setIndexComponentToDelete(null)} block className="cancel">
                            Cancel
                        </Button>
                        <button
                            className="quit"
                            onClick={() => {
                                if (indexComponentToDelete === 0 || !!indexComponentToDelete) {
                                    deleteComponent(indexComponentToDelete);
                                }
                            }}
                        >
                            Delete
                        </button>
                    </div>
                }
            >
                <div className="modal-confirm">
                    <div className="modal-confirm__title" style={{ margin: '0 0 10px 0' }}>
                        Delete component?
                    </div>
                    Are you sure you want to delete{' '}
                    <b>
                        {(indexComponentToDelete === 0 || !!indexComponentToDelete) &&
                            components?.[indexComponentToDelete]?.name}
                    </b>{' '}
                    component?
                </div>
            </Modal>
        </>
    );
};
